import { ChangeEvent, useState } from 'react';
import { useMutation, useQuery } from '@apollo/client/react';

import DeleteModal from '@atom/components/common/DeleteModal';
import MediaLightbox from '@atom/components/common/mediaLightbox/MediaLightbox';
import {
  FILES_TAB_MEDIA_TOTALS,
  GET_MEDIA,
  MEDIA_BULK_DELETE,
  MEDIA_DELETE,
  MEDIA_UPDATE,
} from '@atom/graph/media';
import { useFileDownload } from '@atom/hooks/useFileDownload';
import { useFileUpload } from '@atom/hooks/useFileUpload';
import { Icon, IconButton, ListTable, Progress, Select } from '@atom/mui';
import { DeleteIcon, FileDownloadIcon, FileUploadIcon } from '@atom/mui/icons';
import colors from '@atom/styles/colors';
import {
  MediaConnectionInput,
  MediaType,
  SubjectType,
} from '@atom/types/media';
import { isViewableMedia } from '@atom/utilities/mediaUtilities';
import { removeFromSet, toggleFromSet } from '@atom/utilities/setUtilities';

import ElementMediaTableRow from './ElementMediaTableRow';

import classes from './elementMedia.module.css';

const { MenuItem: SelectMenuItem } = Select;
const { TableHead, TableBody, TableRow, TableCell } = ListTable;

const ALL_FILES = 'All Files';
const MEDIA_LIMIT = 250;
const UPLOAD_WIDTHS = [180, 1440];
const TABLE_HEADERS: { label: string; width?: string }[] = [
  { label: '', width: '3rem' },
  { label: 'name' },
  { label: 'uploaded by' },
  { label: 'uploaded on' },
  { label: '', width: '3rem' },
];

const MEDIA_TYPE_OPTIONS: { label: string; type: MediaType }[] = [
  { label: 'Documents', type: MediaType.DOCUMENT },
  { label: 'Photos', type: MediaType.IMAGE },
  { label: 'Videos', type: MediaType.VIDEO },
];

type ElementMediaPaneProps = {
  subjectId: string;
  parentSubjectId?: string;
  subjectType: SubjectType;
  canCreate: boolean;
  canUpdate: boolean;
  canDelete: boolean;
  coverPhotoMediaId?: string;
  onCoverPhotoToggle?: (mediaId: string) => void;
};

const ElementMediaPane = ({
  subjectId,
  parentSubjectId,
  subjectType,
  canCreate,
  canUpdate,
  canDelete,
  coverPhotoMediaId,
  onCoverPhotoToggle,
}: ElementMediaPaneProps) => {
  const [selectedLabel, setSelectedLabel] = useState<string>(ALL_FILES);
  const [selectedItems, setSelectedItems] = useState<Set<string>>(new Set());
  const [lightboxMediaId, setLightboxMediaId] = useState<string | null>(null);
  const [bulkDeleteOpen, setBulkDeleteOpen] = useState<boolean>(false);

  const selectedType = MEDIA_TYPE_OPTIONS.find(
    option => option.label === selectedLabel,
  )?.type;

  const baseInput: MediaConnectionInput = {
    subjectIds: [subjectId],
    subjectTypes: [subjectType],
    ...(parentSubjectId ? { parentSubjectId } : {}),
  };

  const {
    data: mediaData,
    loading: mediaLoading,
    refetch: refetchMedia,
  } = useQuery(GET_MEDIA, {
    variables: {
      input: {
        ...baseInput,
        limit: MEDIA_LIMIT,
        ...(selectedType ? { type: selectedType } : {}),
      },
    },
    fetchPolicy: 'network-only',
  });

  const { data: totalsData, refetch: refetchTotals } = useQuery(
    FILES_TAB_MEDIA_TOTALS,
    {
      variables: {
        documents: { ...baseInput, limit: 0, type: MediaType.DOCUMENT },
        images: { ...baseInput, limit: 0, type: MediaType.IMAGE },
        videos: { ...baseInput, limit: 0, type: MediaType.VIDEO },
      },
      fetchPolicy: 'network-only',
    },
  );

  const [mediaUpdate] = useMutation(MEDIA_UPDATE);
  const [mediaDelete] = useMutation(MEDIA_DELETE);
  const [mediaBulkDelete] = useMutation(MEDIA_BULK_DELETE);
  const [uploadFiles] = useFileUpload();
  const [downloadFiles] = useFileDownload();

  const media = mediaData?.media?.media ?? [];
  const viewableMedia = media.filter(isViewableMedia);
  const loading = mediaLoading && !mediaData;
  const canMarkAsCoverPhoto = Boolean(onCoverPhotoToggle);
  const coverPhotoFileId = media.find(
    medium => medium.id === coverPhotoMediaId,
  )?.fileId;

  const totalsByLabel: Record<string, number> = {
    Documents: totalsData?.documents?.totalCount ?? 0,
    Photos: totalsData?.images?.totalCount ?? 0,
    Videos: totalsData?.videos?.totalCount ?? 0,
  };

  const typeOptions = [
    ALL_FILES,
    ...MEDIA_TYPE_OPTIONS.filter(
      option =>
        totalsByLabel[option.label] > 0 || option.label === selectedLabel,
    ).map(option => option.label),
  ];

  const refetchAll = () => Promise.all([refetchMedia(), refetchTotals()]);

  const onTypeChange = (event: ChangeEvent<HTMLInputElement>) => {
    setSelectedLabel(event.target.value);
    setSelectedItems(new Set());
  };

  const onToggleCheck = (id: string) =>
    setSelectedItems(current => toggleFromSet(current, id));

  const onUpload = async (event: ChangeEvent<HTMLInputElement>) => {
    await uploadFiles({
      files: event.target.files,
      subjectId,
      subjectType,
      ...(parentSubjectId ? { parentSubjectId } : {}),
      widths: UPLOAD_WIDTHS,
    });

    await refetchAll();
  };

  const onRename = async (id: string, name: string) => {
    await mediaUpdate({ variables: { input: { id, name } } });
    await refetchMedia();
  };

  const onDelete = async (id: string) => {
    await mediaDelete({ variables: { id } });

    setSelectedItems(current => removeFromSet(current, id));
    await refetchAll();
  };

  const onDeleteFromLightbox = async (id: string) => {
    setLightboxMediaId(null);
    await onDelete(id);
  };

  const onBulkDelete = async () => {
    await mediaBulkDelete({
      variables: { input: { ids: [...selectedItems] } },
    });

    setSelectedItems(new Set());
    setBulkDeleteOpen(false);

    await refetchAll();
  };

  const onBulkDownload = () =>
    downloadFiles(media.filter(medium => selectedItems.has(medium.id)));

  const countLabel = selectedLabel === ALL_FILES ? 'Files' : selectedLabel;

  const headerButtons =
    selectedItems.size > 0 ? (
      <div className={classes.headerControls}>
        <IconButton
          onClick={onBulkDownload}
          data-cy="elementMediaBulkDownloadButton"
        >
          <Icon color={colors.neutral.gray} icon={FileDownloadIcon} />
        </IconButton>
        {canDelete && (
          <IconButton
            onClick={() => setBulkDeleteOpen(true)}
            data-cy="elementMediaBulkDeleteButton"
          >
            <Icon color={colors.neutral.gray} icon={DeleteIcon} />
          </IconButton>
        )}
      </div>
    ) : (
      <div className={classes.headerControls}>
        {canCreate && (
          <IconButton component="label" data-cy="elementMediaUploadButton">
            <Icon color={colors.neutral.gray} icon={FileUploadIcon} />
            <input
              type="file"
              className={classes.hiddenInput}
              multiple
              onChange={onUpload}
            />
          </IconButton>
        )}
      </div>
    );

  return (
    <div className={classes.container}>
      <div className={classes.header}>
        <div className={classes.typeFilter}>
          <Select
            value={selectedLabel}
            onChange={onTypeChange}
            style={{ width: 'auto', minWidth: '9rem' }}
            data-cy="elementMediaTypeSelect"
          >
            {typeOptions.map(option => (
              <SelectMenuItem key={option} value={option}>
                {option}
              </SelectMenuItem>
            ))}
          </Select>
          <div className={classes.countText}>
            {`${media.length} ${countLabel}`}
          </div>
        </div>
        {headerButtons}
      </div>
      {loading ? (
        <Progress />
      ) : (
        <ListTable fullHeight={false} data-cy="elementMediaTable">
          <TableHead>
            <TableRow header>
              {TABLE_HEADERS.map((header, index) => (
                <TableCell key={index} variant="head" width={header.width}>
                  {header.label}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {media.map(medium => (
              <ElementMediaTableRow
                key={medium.id}
                medium={medium}
                checked={selectedItems.has(medium.id)}
                canUpdate={canUpdate}
                canDelete={canDelete}
                canMarkAsCoverPhoto={canMarkAsCoverPhoto}
                isCoverPhoto={medium.id === coverPhotoMediaId}
                onToggleCheck={onToggleCheck}
                onRename={onRename}
                onDelete={onDelete}
                onOpen={setLightboxMediaId}
                onCoverPhotoToggle={onCoverPhotoToggle}
              />
            ))}
          </TableBody>
        </ListTable>
      )}
      <MediaLightbox
        open={!!lightboxMediaId}
        media={viewableMedia}
        selectedMediaId={lightboxMediaId}
        canUpdateMedia={canUpdate}
        canDeleteMedia={canDelete}
        canMarkAsCoverPhoto={canMarkAsCoverPhoto}
        coverPhotoFileId={coverPhotoFileId}
        onCoverPhotoToggle={onCoverPhotoToggle}
        onClose={() => setLightboxMediaId(null)}
        onSelectedMediaChange={setLightboxMediaId}
        onRename={onRename}
        onDelete={onDeleteFromLightbox}
        onRefetch={refetchAll}
      />
      <DeleteModal
        open={bulkDeleteOpen}
        title="Delete Files"
        content="Are you sure you want to delete these files?"
        onCancel={() => setBulkDeleteOpen(false)}
        onConfirm={onBulkDelete}
      />
    </div>
  );
};

export default ElementMediaPane;
